import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Deals, DealAccessApi } from "@/api/endpoints";
import type { Deal, DealUpdate, Stage, Blocker } from "@/api/types";
import { eur, fmtDay } from "@/lib/format";
import { apiErrorMessage } from "@/api/client";
import { useAuthStore } from "@/store/auth";
import AccountPicker from "@/components/AccountPicker";
import AttributionFields from "@/components/AttributionFields";
import { toast } from "@/lib/feedback";

interface Props {
  dealId: string;
  stages: Stage[];
  blockers: Blocker[];
}

export default function DealInfoPanel({ dealId, stages, blockers }: Props) {
  const qc = useQueryClient();
  const [form, setForm] = useState<Partial<Deal>>({});

  const me = useAuthStore((s) => s.user);

  const dealQuery = useQuery({
    queryKey: ["deal", dealId],
    queryFn: () => Deals.get(dealId),
    enabled: !!dealId,
  });

  const accessQuery = useQuery({
    queryKey: ["deal-access", dealId],
    queryFn: () => DealAccessApi.list(dealId),
    enabled: !!dealId,
  });

  const myGrant = (accessQuery.data ?? []).find((g) => g.user_id === me?.id);
  const canEdit = me?.role === "admin" || myGrant?.capacity === "owner";
  const inputClass = `input${canEdit ? "" : " disabled:opacity-60 disabled:cursor-not-allowed"}`;

  useEffect(() => {
    setForm(dealQuery.data ?? {});
  }, [dealQuery.data]);

  const [savedAt, setSavedAt] = useState<number | null>(null);
  const saveMutation = useMutation({
    mutationFn: (payload: DealUpdate) => Deals.update(dealId, payload),
    onSuccess: () => {
      setSavedAt(Date.now());
      qc.invalidateQueries({ queryKey: ["deal", dealId] });
      qc.invalidateQueries({ queryKey: ["deals"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      qc.invalidateQueries({ queryKey: ["my-work"] });
    },
    onError: (e) => toast.err(`Save failed: ${apiErrorMessage(e)}`),
  });

  const update = (patch: Partial<Deal>) => {
    if (!canEdit) return;
    setForm((f) => ({ ...f, ...patch }));
    saveMutation.mutate(patch as DealUpdate);
  };

  // Fade the "Saved" note out a couple of seconds after each save.
  useEffect(() => {
    if (savedAt == null) return;
    const id = window.setTimeout(() => setSavedAt(null), 2500);
    return () => window.clearTimeout(id);
  }, [savedAt]);

  const deal = dealQuery.data;

  if (!deal) return <div className="p-5 text-mut-300">Loading…</div>;

  const saveNote = saveMutation.isPending
    ? "Saving…"
    : saveMutation.isError
      ? "Couldn't save the last change"
      : savedAt != null
        ? "Saved"
        : null;

  return (
    <div className="space-y-6">
      {/* Headline metrics */}
      <div className="grid grid-cols-3 gap-3">
        <div className="panel-tight p-3">
          <div className="label">ARR</div>
          <div className="font-semibold">{eur(deal.arr)}</div>
        </div>
        <div className="panel-tight p-3">
          <div className="label">Year-1</div>
          <div className="font-semibold">{eur(deal.year_one_total)}</div>
        </div>
        <div className="panel-tight p-3">
          <div className="label">Weighted</div>
          <div className="font-semibold">{eur(deal.weighted_arr)}</div>
        </div>
      </div>

      {/* Editable fields */}
      <section className="space-y-3">
        <div className="flex items-center justify-between min-h-[20px]">
          {!canEdit ? (
            <span className="chip bg-fg/[0.05] text-mut-300">View only</span>
          ) : (
            <span className="text-xs text-mut-500">Changes save as you go</span>
          )}
          {saveNote && (
            <span
              role="status"
              className={`text-xs font-medium ${saveMutation.isError ? "text-accent-pink" : "text-accent-green"}`}
            >
              {saveNote}
            </span>
          )}
        </div>
        <div>
          <label className="label">Lead</label>
          <input
            className={inputClass}
            disabled={!canEdit}
            value={form.lead ?? ""}
            onChange={(e) => setForm({ ...form, lead: e.target.value })}
            onBlur={() => form.lead !== deal.lead && update({ lead: form.lead })}
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">Stage</label>
            <select
              className={inputClass}
              disabled={!canEdit}
              value={form.stage ?? ""}
              onChange={(e) => update({ stage: e.target.value })}
            >
              {stages.map((s) => (
                <option key={s.id} value={s.name}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="label">Probability (%)</label>
            <input
              type="number"
              min={0}
              max={100}
              step={5}
              className={inputClass}
              disabled={!canEdit}
              value={form.probability == null ? "" : Math.round(form.probability * 100)}
              onChange={(e) =>
                setForm({
                  ...form,
                  probability: e.target.value
                    ? Math.min(100, Math.max(0, Number(e.target.value))) / 100
                    : null,
                })
              }
              onBlur={() => {
                if (form.probability !== deal.probability)
                  update({ probability: form.probability });
              }}
            />
          </div>
        </div>

        <div>
          <label className="label">Account</label>
          <AccountPicker
            value={form.account_id ?? null}
            currentName={deal.account_name}
            disabled={!canEdit}
            onChange={(account_id) => update({ account_id })}
          />
        </div>

        <AttributionFields
          value={{
            sourcer_user_id: form.sourcer_user_id ?? null,
            sourcer_contact_id: form.sourcer_contact_id ?? null,
            closer_user_id: form.closer_user_id ?? null,
          }}
          onChange={update}
          disabled={!canEdit}
          className={inputClass}
          sourcerName={deal.sourcer_name}
          closerName={deal.closer_name}
        />

        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="label">Monthly fee (€)</label>
            <input
              type="number"
              className={inputClass}
              disabled={!canEdit}
              value={form.monthly_fee ?? ""}
              onChange={(e) =>
                setForm({
                  ...form,
                  monthly_fee: e.target.value ? Number(e.target.value) : null,
                })
              }
              onBlur={() =>
                form.monthly_fee !== deal.monthly_fee && update({ monthly_fee: form.monthly_fee })
              }
            />
          </div>
          <div>
            <label className="label">One-time (€)</label>
            <input
              type="number"
              className={inputClass}
              disabled={!canEdit}
              value={form.one_time_fee ?? ""}
              onChange={(e) =>
                setForm({
                  ...form,
                  one_time_fee: e.target.value ? Number(e.target.value) : null,
                })
              }
              onBlur={() =>
                form.one_time_fee !== deal.one_time_fee &&
                update({ one_time_fee: form.one_time_fee })
              }
            />
          </div>
          <div>
            <label className="label">ARR (€)</label>
            <div
              className="input flex items-center text-mut-300 cursor-not-allowed select-none"
              title="Derived: monthly fee × 12"
            >
              {eur((form.monthly_fee ?? 0) * 12)}
            </div>
            <p className="text-xs text-mut-500 mt-1">Auto from monthly fee</p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">Due date</label>
            <input
              type="date"
              className={inputClass}
              disabled={!canEdit}
              value={form.due_date ?? ""}
              onChange={(e) => update({ due_date: e.target.value || null })}
            />
          </div>
          <div>
            <label className="label">Last contact</label>
            <input
              type="date"
              className={inputClass}
              disabled={!canEdit}
              value={form.last_contact ?? ""}
              onChange={(e) => update({ last_contact: e.target.value || null })}
            />
          </div>
        </div>

        <div>
          <label className="label">Blocker</label>
          <select
            className={inputClass}
            disabled={!canEdit}
            value={form.blocker ?? ""}
            onChange={(e) => update({ blocker: e.target.value || null })}
          >
            <option value="">—</option>
            {blockers.map((b) => (
              <option key={b.id} value={b.name}>
                {b.name}
              </option>
            ))}
          </select>
        </div>
      </section>

      {/* Footer metadata */}
      <div className="text-xs text-mut-300 pt-4 border-t border-fg/[0.05] flex justify-between">
        <span>
          Stage since {fmtDay(deal.stage_since)} ({deal.days_in_stage ?? "—"} days)
        </span>
        <span>Updated {fmtDay(deal.updated_at)}</span>
      </div>
    </div>
  );
}
